<template>
  <b-modal
    :active="true"
    aria-role="dialog"
    aria-modal
    trap-focus
    scroll="keep"
    width="80%"
  >
    <div class="modal-card">
      <header class="modal-card-head">
        <div>Add Instrumentation</div>
        <div class="modal-card-head-right">
          <b-button
            v-if="!showQRCodeReader"
            :icon-left="'camera'"
            :disabled="isTaskCompleted"
            @click="toggleQrReader"
          >
            {{ 'Add Using Camera' }}
          </b-button>

          <form v-on:submit.prevent="onSearch">
            <b-field>
              <b-input
                ref="searchInput"
                placeholder="Search"
                v-model="search"
                icon="magnify"
                :icon-right="search !== '' ? 'close' : null"
                icon-right-clickable
                @icon-right-click="clearIconClick"
                @keyup.enter.native="onSearchEnter"
              />
            </b-field>
          </form>
        </div>
      </header>

      <section class="modal-card-body">
        <QRCodeReaderVue
          v-if="showQRCodeReader"
          @data="onQRDecode"
          :selectQrCode="true"
        />
        <template v-else>
          <b-table
            :data="instrumentations"
            :narrowed="true"
            :loading="isLoading"
            :paginated="instrumentations.length > 20 ? true : false"
            :per-page="20"
            checkable
            :sticky-header="true"
            :header-checkable="false"
            :checked-rows.sync="checkedRows"
            :mobile-cards="false"
          >
            <template slot-scope="props">
              <b-table-column
                field="instrumentId"
                label="Instrument ID"
                sortable
              >
                {{ props.row.instrumentId }}
              </b-table-column>

              <b-table-column field="instrumentName" label="Name" sortable>
                {{ props.row.instrumentName }}
              </b-table-column>

              <b-table-column field="typeName" label="Type Name" sortable>
                {{ props.row.typeName }}
              </b-table-column>

              <b-table-column
                field="lastCalibrationDate"
                label="Last Calibration Date"
                centered
                sortable
              >
                {{
                  props.row.lastCalibrationDate
                    ? new Date(
                        props.row.lastCalibrationDate
                      ).toLocaleDateString()
                    : ''
                }}
              </b-table-column>

              <b-table-column
                field="calibrationDueDate"
                label="Calibration Due Date"
                centered
                sortable
              >
                {{
                  props.row.calibrationDueDate
                    ? new Date(
                        props.row.calibrationDueDate
                      ).toLocaleDateString()
                    : ''
                }}
              </b-table-column>
            </template>
            <template #empty>
              <div class="has-text-centered">No records</div>
            </template>
            <template #bottom-left> </template>
          </b-table>

          <div class="selected-instruments">
            <b-field
              v-for="selectedInst in selectedInstruments"
              :key="selectedInst._id"
            >
              <b-tag
                attached
                closable
                aria-close-label="Close tag"
                @close="removeTag(selectedInst)"
              >
                {{ selectedInst.instrumentId || selectedInst._id }}:
                {{ selectedInst.instrumentName || '' }}
              </b-tag>
            </b-field>
          </div>
        </template>
      </section>

      <footer class="modal-card-foot">
        <b-button
          v-if="showQRCodeReader"
          :icon-left="'camera-off'"
          :disabled="isTaskCompleted"
          @click="toggleQrReader"
        >
          {{ 'Close Camera' }}
        </b-button>

        <b-button @click="closeModal" v-else>
          {{ $t('cancel') }}
        </b-button>

        <b-button
          type="is-success"
          @click="onSelectDetectedQrCode"
          v-if="showQRCodeReader && detectedQrCode"
        >
          Select {{ detectedQrCode }}
        </b-button>

        <b-button type="is-success" @click="onSave" v-else>
          {{ $t('save') }}
        </b-button>
      </footer>
    </div>
    <OutOfCalibrationDateModal
      v-if="showOutOfCalibration"
      @onClose="showOutOfCalibration = false"
      @onSave="onSaveWithOutOfCalibration"
    />
  </b-modal>
</template>

<script>
import { GET_CURRENT_JOB } from '../../../store/jobs/getters/getterTypes';
import {
  FETCH_INSTRUMENTATIONS,
  FETCH_INSTRUMENTATION_TASKS,
} from '../../../store/instrumentations/actions/actionTypes';
import {
  GET_INSTRUMENTATIONS,
  GET_INSTRUMENTATIONS_TASK,
} from '../../../store/instrumentations/getters/getterTypes';

import { GET_CURRENT_TASK } from '../../../store/tasks/getters/getterTypes';
import QRCodeReaderVue from '../../layout/QRCodeReader';
import OutOfCalibrationDateModal from './OutOfCalibrationDateModal';

export default {
  components: {
    QRCodeReaderVue,
    OutOfCalibrationDateModal,
  },

  props: {
    type: {
      type: String,
    },
  },

  computed: {
    job() {
      return this.$store.getters[GET_CURRENT_JOB];
    },
    instrumentations() {
      return this.$store.getters[GET_INSTRUMENTATIONS] || [];
    },

    instrumentationsTask() {
      return this.$store.getters[GET_INSTRUMENTATIONS_TASK] || [];
    },

    currentTask() {
      return this.$store.getters[GET_CURRENT_TASK];
    },

    checkedRows: {
      get() {
        const ids = this.selectedIds;
        return this.instrumentations.filter((row) => ids.has(row._id));
      },
      set(newCheckedRows) {
        const currentIds = new Set(this.instrumentations.map((r) => r._id));

        Array.from(currentIds).forEach((id) => {
          if (this.selectedIds.has(id)) this.selectedIds.delete(id);
        });

        (newCheckedRows || []).forEach((row) => {
          this.selectedIds.add(row._id);
          this.selectedById[row._id] = row;
        });

        this.selectedIds = new Set(this.selectedIds);
        this.selectedById = { ...this.selectedById };
      },
    },

    selectedInstruments() {
      return Array.from(this.selectedIds).map((id) => {
        return (
          this.selectedById[id] ||
          this.instrumentations.find((r) => r._id === id) || { _id: id }
        );
      });
    },
  },

  data() {
    return {
      isTaskCompleted: false,
      search: '',
      selectedIds: new Set(),
      selectedById: {},
      isLoading: false,
      showQRCodeReader: false,
      seededFromLinks: false,
      showOutOfCalibration: false,
      detectedQrCode: undefined,
    };
  },

  watch: {
    instrumentationsTask: {
      handler() {
        if (this.seededFromLinks) return;

        (this.instrumentationsTask || []).forEach((link) => {
          if (link?.instrumentationId)
            this.selectedIds.add(link.instrumentationId);
        });

        this.selectedIds = new Set(this.selectedIds);
        this.seededFromLinks = true;
      },
      immediate: true,
      deep: true,
    },

    instrumentations: {
      handler() {
        (this.instrumentations || []).forEach((row) => {
          if (this.selectedIds.has(row._id)) {
            this.selectedById[row._id] = row;
          }
        });
        this.selectedById = { ...this.selectedById };
      },
      immediate: true,
      deep: false,
    },
  },

  async mounted() {
    this.detectedQrCode = undefined;
    await this.getInstrumentationsTask();
    await this.getInstrumentations();
    this.seedSelectionFromLinks();
  },

  methods: {
    onSearchEnter() {
      this.onSearch();
      this.$refs.searchInput?.$el?.querySelector('input')?.blur();
    },

    seedSelectionFromLinks() {
      const nextIds = new Set();
      (this.instrumentationsTask || []).forEach((link) => {
        if (link?.instrumentationId) nextIds.add(link.instrumentationId);
      });
      const nextById = { ...this.selectedById };
      (this.instrumentations || []).forEach((row) => {
        if (nextIds.has(row._id)) nextById[row._id] = row;
      });

      this.selectedIds = nextIds;
      this.selectedById = nextById;
    },

    onQRDecode(data) {
      this.detectedQrCode = data;
    },

    onSelectDetectedQrCode() {
      if (!this.detectedQrCode) return;

      this.search = this.detectedQrCode;
      this.showQRCodeReader = false;
      this.detectedQrCode = undefined;
      this.onSearch();
    },

    toggleQrReader() {
      this.showQRCodeReader = !this.showQRCodeReader;
      if (!this.showQRCodeReader) {
        this.detectedQrCode = undefined;
      }
    },

    async getInstrumentations(query) {
      this.isLoading = true;
      try {
        await this.$store.dispatch(FETCH_INSTRUMENTATIONS, {
          siteId: this.job.executingSite,
          query,
        });
      } catch (error) {
        console.error('Error fetching instrumentations:', error);
      } finally {
        this.isLoading = false;
      }
    },

    async getInstrumentationsTask() {
      this.isLoading = true;
      try {
        await this.$store.dispatch(FETCH_INSTRUMENTATION_TASKS, {
          taskId: this.currentTask._id,
        });
      } catch (error) {
        console.error('Error fetching instrumentations linked to task:', error);
      } finally {
        this.isLoading = false;
      }
    },

    onSearch() {
      this.getInstrumentations({
        search: this.search,
      });
    },

    removeTag(selectedInst) {
      this.selectedIds.delete(selectedInst._id);
      delete this.selectedById[selectedInst._id];
      this.selectedIds = new Set(this.selectedIds);
      this.selectedById = { ...this.selectedById };
    },

    clearIconClick() {
      this.search = '';
      this.getInstrumentations();
    },

    closeModal() {
      this.$emit('close');
    },

    onSaveWithOutOfCalibration() {
      const selected = Array.from(this.selectedIds).map((id) => {
        return (
          this.selectedById[id] ||
          this.instrumentations.find((r) => r._id === id) || { _id: id }
        );
      });
      this.$emit('save', selected);
      this.showOutOfCalibration = false;
    },

    onSave() {
      const selected = Array.from(this.selectedIds).map((id) => {
        return (
          this.selectedById[id] ||
          this.instrumentations.find((r) => r._id === id) || { _id: id }
        );
      });

      let hasOutOfCalibration = false;
      const currentDate = new Date();
      selected.forEach((instrument) => {
        if (instrument.calibrationDueDate) {
          const calibrationDueDate = new Date(instrument.calibrationDueDate);
          if (calibrationDueDate < currentDate) {
            hasOutOfCalibration = true;
          }
        }
      });

      if (hasOutOfCalibration) {
        this.showOutOfCalibration = true;
        return;
      }

      this.$emit('save', selected);
    },
  },
};
</script>

<style scoped scss>
.modal-card-foot {
  display: flex;
  justify-content: space-between;
  gap: 10px;
}
.modal-card-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-card-head-right {
  display: flex;
  gap: 10px;
}

.modal-card {
  width: 100%;
}

.selected-instruments {
  display: flex;
  flex-wrap: wrap;
  margin-top: 15px;
  gap: 5px;
}
</style>
